import { useMemo, useState } from "react";
import { Link, getRouteApi } from "@tanstack/react-router";
import { diffModels } from "@shared/roi/diff";
import { toScenarioModel, type ScenarioDetail } from "@shared/schemas/scenario";
import { Badge } from "@client/components/Badge";
import { Button } from "@client/components/Button";
import { ConfirmDialog } from "@client/components/ConfirmDialog";
import { DiffTable } from "@client/components/DiffTable";
import { EmptyState } from "@client/components/EmptyState";
import { ErrorNotice, LoadingBlock } from "@client/components/Notices";
import { Panel } from "@client/components/Panel";
import { VersionTimeline } from "@client/components/VersionTimeline";
import {
  useRestoreScenarioVersion,
  useScenarioVersion,
  useScenarioVersions,
} from "@client/hooks/useScenarioVersions";
import { useScenario } from "@client/hooks/useScenarios";
import { formatDateTime } from "@client/lib/datetime";
import { describeError } from "@client/lib/errorMessage";

const route = getRouteApi("/_authed/products/$slug/scenarios/$scenarioId/versions");

/**
 * Version history and restore — docs/08-frontend.md#version-history.
 *
 * Versions on the left, a field-level diff of the selected one against the
 * scenario as it stands on the right. Restoring saves the old model *forward* as
 * a new version; the history is never rewritten, and the page says so next to
 * the button rather than in a tooltip nobody opens.
 */
export function ScenarioVersionsPage() {
  const { slug, scenarioId } = route.useParams();
  const scenario = useScenario(scenarioId);

  if (scenario.isPending) return <LoadingBlock rows={5} label="Loading scenario" />;
  if (scenario.isError) {
    return <ErrorNotice error={scenario.error} onRetry={() => void scenario.refetch()} />;
  }

  return <VersionHistory key={scenario.data.id} detail={scenario.data} slug={slug} />;
}

function VersionHistory({ detail, slug }: { detail: ScenarioDetail; slug: string }) {
  const versions = useScenarioVersions(detail.id);
  const restore = useRestoreScenarioVersion(detail.id);

  const rows = useMemo(() => versions.data?.items ?? [], [versions.data]);
  const [selected, setSelected] = useState<number | null>(null);

  // Nothing is selected until the list arrives, and then the newest *older*
  // version is: a diff of the current version against itself is an empty table,
  // and the question people open this page with is "what did the last save do".
  const selectedVersion =
    selected ??
    rows.find((row) => row.version !== detail.version)?.version ??
    rows[0]?.version ??
    null;

  const snapshot = useScenarioVersion(detail.id, selectedVersion);
  const [confirming, setConfirming] = useState<number | null>(null);

  const diff = useMemo(() => {
    if (!snapshot.data) return null;
    return diffModels(
      toScenarioModel(snapshot.data.snapshot, snapshot.data.snapshot.currencyCode),
      toScenarioModel(detail.model, detail.model.currencyCode),
    );
  }, [snapshot.data, detail.model]);

  const selectedRow = rows.find((row) => row.version === selectedVersion);
  const canRestore = detail.abilities.canEdit && selectedVersion !== null;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Link
            to="/products/$slug/scenarios/$scenarioId"
            params={{ slug, scenarioId: detail.id }}
            className="text-xs font-semibold text-azure-deep hover:underline"
          >
            ← Back to the editor
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-ink">{detail.name}</h1>
            <Badge kind="neutral">History</Badge>
          </div>
          <p className="text-xs text-ink-soft">
            Now at version {detail.version} · last saved {formatDateTime(detail.updatedAt)}
          </p>
        </div>
      </header>

      {versions.isPending && <LoadingBlock rows={4} label="Loading versions" />}
      {versions.isError && (
        <ErrorNotice error={versions.error} onRetry={() => void versions.refetch()} />
      )}

      {versions.isSuccess && rows.length === 0 && (
        <EmptyState
          title="No versions yet"
          description="A version is written every time this scenario is saved. Save it once and its history starts here."
        />
      )}

      {rows.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
          <Panel
            title="Versions"
            description={`The last ${rows.length} saves, newest first.`}
            className="self-start"
          >
            <VersionTimeline
              versions={rows}
              currentVersion={detail.version}
              selected={selectedVersion}
              onSelect={setSelected}
              currencyCode={detail.currencyCode}
              horizonYears={detail.horizonYears}
            />
          </Panel>

          <Panel
            title={
              selectedRow
                ? `Version ${selectedRow.version} against version ${detail.version}`
                : "Select a version"
            }
            description={
              selectedRow?.version === detail.version
                ? "This is the version you are looking at in the editor, so there is nothing to compare it with."
                : "Every input that differs, with the older figure on the left."
            }
            actions={
              canRestore &&
              selectedVersion !== detail.version && (
                <Button
                  size="sm"
                  disabled={restore.isPending}
                  onClick={() => setConfirming(selectedVersion)}
                >
                  {restore.isPending ? "Restoring…" : `Restore version ${selectedVersion}`}
                </Button>
              )
            }
          >
            {snapshot.isPending && <LoadingBlock rows={3} label="Loading version" />}
            {snapshot.isError && (
              <ErrorNotice error={snapshot.error} onRetry={() => void snapshot.refetch()} />
            )}
            {restore.isError && <ErrorNotice error={restore.error} title="The restore failed" />}

            {diff && (
              <DiffTable
                diff={diff}
                currencyCode={detail.currencyCode}
                beforeLabel={`Version ${selectedVersion}`}
                afterLabel={`Version ${detail.version}`}
                emptyMessage={
                  selectedVersion === detail.version
                    ? "This is the current version."
                    : "Nothing differs — this save changed only the label, or changed a value back to what it was."
                }
              />
            )}
          </Panel>
        </div>
      )}

      <ConfirmDialog
        open={confirming !== null}
        title={`Restore version ${confirming ?? ""}?`}
        body={
          <>
            The scenario goes back to how it was at version {confirming}, saved forward as version{" "}
            {detail.version + 1}. Nothing in this history is rewritten, and version {detail.version}{" "}
            stays here to restore in turn if this was a mistake.
          </>
        }
        confirmLabel="Restore it"
        busy={restore.isPending}
        error={restore.isError ? describeError(restore.error) : null}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming === null) return;
          restore.mutate(confirming, {
            onSuccess: () => {
              setConfirming(null);
              // The restore is now the newest version; selecting it would show an
              // empty diff, so the selection is released and the default — the
              // newest older version — takes over again.
              setSelected(null);
            },
          });
        }}
      />
    </div>
  );
}
